import type { LucideIcon } from "lucide-react";
import { Sparkles } from "lucide-react";
import { cn } from "../../client/utils";
import ScrollAnimation from "../../landing-page/components/ScrollAnimation";

/**
 * Feature-detail layout: a hero screenshot followed by a bento-style card grid.
 * Each bullet from the page copy becomes a card with the same effects as the
 * homepage FeaturesGrid (HUD corners, ambient hover glow, top accent line,
 * coloured icon). Replaces the old flat FeatureList on the marketing feature
 * pages.
 */

// Cycled accent palette — mirrors the homepage FeaturesGrid colours.
const ACCENTS = ["#A855F7", "#3D6EFF", "#00C9A7", "#F59E0B", "#EC4899"];

interface FeatureCardGridProps {
  bullets: string[];
  image?: string;
  imageAlt?: string;
  // Per-page icons, cycled if fewer than the number of bullets.
  icons?: LucideIcon[];
}

// Split a bullet into a bold title + description when it contains a colon
// ("Vue globale : total payouts…"). Otherwise the whole bullet is the body.
function splitBullet(bullet: string): { title?: string; body: string } {
  const idx = bullet.indexOf(":");
  if (idx > 0 && idx < bullet.length - 1) {
    const title = bullet.slice(0, idx).trim();
    const body = bullet.slice(idx + 1).trim();
    // Only treat as a title if it's short enough to read as a label.
    if (title.length <= 40) return { title, body };
  }
  return { body: bullet };
}

export default function FeatureCardGrid({
  bullets,
  image,
  imageAlt,
  icons,
}: FeatureCardGridProps) {
  return (
    <section className="px-6 py-12 lg:px-8">
      <div className="mx-auto max-w-7xl">
        {/* Hero screenshot */}
        {image && (
          <ScrollAnimation direction="up">
            <div className="relative mx-auto mb-14 max-w-5xl">
              {/* HUD corner decorations */}
              <div className="pointer-events-none absolute -left-2 -top-2 z-20 h-5 w-5 rounded-tl-sm border-l-2 border-t-2 border-primary/50" />
              <div className="pointer-events-none absolute -right-2 -top-2 z-20 h-5 w-5 rounded-tr-sm border-r-2 border-t-2 border-primary/50" />
              <div className="pointer-events-none absolute -bottom-2 -left-2 z-20 h-5 w-5 rounded-bl-sm border-b-2 border-l-2 border-primary/50" />
              <div className="pointer-events-none absolute -bottom-2 -right-2 z-20 h-5 w-5 rounded-br-sm border-b-2 border-r-2 border-primary/50" />

              <div className="relative overflow-hidden rounded-xl border border-primary/20 bg-card shadow-2xl shadow-primary/10">
                <div className="pointer-events-none absolute -inset-px z-10 rounded-xl bg-gradient-to-br from-primary/10 via-transparent to-primary/5" />
                <img
                  src={image}
                  alt={imageAlt ?? ""}
                  loading="lazy"
                  className="w-full object-cover object-top"
                />
              </div>
            </div>
          </ScrollAnimation>
        )}

        {/* Card grid */}
        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          {bullets.map((bullet, index) => {
            const { title, body } = splitBullet(bullet);
            const accent = ACCENTS[index % ACCENTS.length];
            const Icon = icons?.length
              ? icons[index % icons.length]
              : Sparkles;

            return (
              <ScrollAnimation key={bullet} direction="up" delay={index * 60}>
                <FeatureCard
                  title={title}
                  body={body}
                  accent={accent}
                  Icon={Icon}
                />
              </ScrollAnimation>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function FeatureCard({
  title,
  body,
  accent,
  Icon,
}: {
  title?: string;
  body: string;
  accent: string;
  Icon: LucideIcon;
}) {
  return (
    <div
      className={cn(
        "group relative flex h-full min-h-[180px] flex-col overflow-hidden rounded-xl",
        "border border-border/40 bg-card/50 backdrop-blur-sm",
        "cursor-default transition-all duration-300",
        "hover:border-border/80 hover:bg-card/80",
      )}
    >
      {/* HUD corners */}
      <div
        className="pointer-events-none absolute left-0 top-0 h-4 w-4 border-l border-t opacity-40 transition-opacity duration-300 group-hover:opacity-80"
        style={{ borderColor: accent }}
      />
      <div
        className="pointer-events-none absolute bottom-0 right-0 h-4 w-4 border-b border-r opacity-40 transition-opacity duration-300 group-hover:opacity-80"
        style={{ borderColor: accent }}
      />

      {/* Ambient glow on hover */}
      <div
        className="pointer-events-none absolute -right-16 -top-16 h-32 w-32 rounded-full opacity-0 blur-3xl transition-opacity duration-500 group-hover:opacity-100"
        style={{ backgroundColor: `${accent}22` }}
      />

      {/* Top accent line */}
      <div
        className="absolute left-0 right-0 top-0 h-px opacity-0 transition-opacity duration-300 group-hover:opacity-100"
        style={{
          background: `linear-gradient(90deg, transparent, ${accent}60, transparent)`,
        }}
      />

      <div className="relative z-10 flex h-full flex-col p-6">
        {/* Icon */}
        <div
          className="mb-4 flex h-10 w-10 items-center justify-center rounded-lg border transition-all duration-300 group-hover:scale-110"
          style={{
            backgroundColor: `${accent}14`,
            borderColor: `${accent}30`,
          }}
        >
          <Icon className="h-5 w-5" style={{ color: accent }} />
        </div>

        {title && (
          <h3 className="mb-2 text-base font-semibold leading-snug text-foreground">
            {title}
          </h3>
        )}
        <p className="flex-1 text-sm leading-relaxed text-muted-foreground">
          {body}
        </p>
      </div>
    </div>
  );
}
